import { D, shortDate } from '../lib/dates';
import { fmtN } from '../lib/data';

/* График по датам (P0): линия с заливкой, точки, сетка с подписями справа.
   Шкала круглыми числами: шаг 1, 2, 2,5 или 5 нужного порядка. */
export function Chart({ series, unit, today }: { series: [string, number][]; unit: string; today: string }) {
  const W = 320,
    H = 150,
    L = 6,
    R = 42,
    T = 14,
    B = 24;
  const xs = series.map((s) => D(s[0]).getTime());
  const ys = series.map((s) => s[1]);
  let lo = Math.min(...ys),
    hi = Math.max(...ys);
  if (hi === lo) {
    hi += 1;
    lo -= 1;
  }
  const raw = (hi - lo) / 2;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((x) => x >= raw)!;
  const ticks: number[] = [];
  for (let t = Math.floor(lo / step) * step; t <= hi + step * 0.999; t += step) ticks.push(Math.round(t * 100) / 100);
  lo = ticks[0];
  hi = ticks[ticks.length - 1];
  const x0 = xs[0],
    x1 = xs[xs.length - 1];
  const X = (t: number) => L + (W - L - R) * (x1 === x0 ? 0.5 : (t - x0) / (x1 - x0));
  const Y = (v: number) => T + (H - T - B) * (1 - (v - lo) / (hi - lo));
  const pts = series.map((s, i) => [X(xs[i]), Y(s[1])]);
  const line = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  /* Подписи дат: последняя всегда, остальные — если не налезают на соседнюю (близкие даты на длинной шкале);
     крайние прижаты к краю, чтобы не обрезались */
  const GAP = 48;
  const lab: [number, string][] = [];
  const cand = series.length <= 6 ? series.map((_, i) => i) : [0, series.length - 1];
  for (let j = cand.length - 1; j >= 0; j--) {
    const i = cand[j];
    if (lab.length && pts[lab[0][0]][0] - pts[i][0] < GAP) continue;
    lab.unshift([i, series[i][0]]);
  }
  const anchor = (x: number) => (x - L < GAP / 2 ? 'start' : W - R - x < GAP / 2 ? 'end' : 'middle');
  return (
    <svg className="chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={'График, ' + unit}>
      <defs>
        <linearGradient id="chg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FC5200" stopOpacity=".22" />
          <stop offset="1" stopColor="#FC5200" stopOpacity="0" />
        </linearGradient>
      </defs>
      <g className="gd">
        {ticks.map((v) => {
          const y = Y(v);
          return (
            <g key={v}>
              <line x1={L} x2={W - R + 6} y1={y.toFixed(1)} y2={y.toFixed(1)} />
              <text x={W - R + 10} y={(y + 4).toFixed(1)}>
                {fmtN(v)}
              </text>
            </g>
          );
        })}
      </g>
      <path className="ar" d={`${line} L${pts[pts.length - 1][0].toFixed(1)} ${H - B} L${pts[0][0].toFixed(1)} ${H - B} Z`} />
      <path className="ln" d={line} />
      {pts.map((p, i) => (
        <circle key={i} className={i === pts.length - 1 ? 'last' : ''} cx={p[0].toFixed(1)} cy={p[1].toFixed(1)} r={i === pts.length - 1 ? 4.5 : 3.2} />
      ))}
      <g className="xl2">
        {lab.map(([i, d]) => (
          <text key={i} x={pts[i][0].toFixed(1)} y={H - 6} textAnchor={anchor(pts[i][0])}>
            {shortDate(d, today)}
          </text>
        ))}
      </g>
    </svg>
  );
}

/** Изменение со знаком: «+17,5 кг», «−1,2 см» */
export const delta = (d: number, unit: string, dec?: boolean) => (!d ? '' : (d > 0 ? '+' : '−') + fmtN(Math.abs(d), dec) + (unit === '%' ? ' %' : ' ' + unit));
export const dltCls = (d: number) => (d > 0 ? 'up' : d < 0 ? 'dn' : '');
